import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { deleteResource } from "@/features/Resources/actions/deleteResource";
import { renameResource } from "@/features/Resources/actions/renameResource";
import { useTeamMembers } from "@/features/Teams/hooks/useTeamMembers";
import { delete_connector_event, open_connector_modal_event, rename_connector_event } from "@/services/posthog/events";
import { useAnalytics } from "@/services/posthog/hooks";
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { ConnectorType } from "@pufflig/prompt-studio-types";
import { DeleteConfirmationModal, Icon, MenuItem, RenameItemModal, SectionHeader } from "@pufflig/prompt-studio-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConnectorConfigModal } from "../components/ConnectorConfigModal/ConnectorConfigModal";
import { ConnectorModal } from "../components/ConnectorModal/ConnectorModal";
import { ConnectorTable } from "../components/ConnectorTable/ConnectorTable";
import { useConnectors } from "../hooks/useConnectors";

export const DataPage = () => {
  const [searchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(0);
  const { isOpen: isAddConnectorOpen, onOpen: onOpenAddConnector, onClose: onCloseAddConnector } = useDisclosure();
  const {
    isOpen: isConnectorConfigOpen,
    onOpen: _onOpenConnectorConfigModal,
    onClose: onCloseConnectorConfig,
  } = useDisclosure();
  const [selectedConnectorType, setSelectedConnectorType] = useState<ConnectorType | null>(null);
  const onOpenConnectorConfigModal = (connectorType: ConnectorType) => {
    setSelectedConnectorType(connectorType);
    _onOpenConnectorConfigModal();
  };
  const teamId = searchParams.get("team") || undefined;
  const { data: connectors, isLoading } = useConnectors(teamId);
  const [connectorToDelete, setConnectorToDelete] = useState<string | null>(null);
  const [connectorToRename, setConnectorToRename] = useState<string | null>(null);
  const { isOpen: isDeleteModalOpen, onClose: onCloseDeleteModal, onOpen: onOpenDeleteModal } = useDisclosure();
  const { isOpen: isRenameModalOpen, onClose: onCloseRenameModal, onOpen: onOpenRenameModal } = useDisclosure();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const { navigateToSemanticModel } = useAppNavigation();

  useEffect(() => {
    const tabIndexString = searchParams.get("tab");
    if (tabIndexString !== null) {
      if (tabIndexString === "custom") setTabIndex(1);
    } else setTabIndex(0);
  }, [searchParams]);

  const { data: teamMembers } = useTeamMembers(teamId || undefined);

  const tableData =
    connectors?.map((connector) => ({
      id: connector.resource_id,
      type: connector.type,
      name: connector.name,
      owner: {
        name: teamMembers?.[connector.created_by]?.name || teamMembers?.[connector.created_by]?.email || "",
        picture: teamMembers?.[connector.created_by]?.picture || "",
      },
      createdAt: connector.created_at,
    })) || [];

  return (
    <Flex direction="column" flex="1" paddingX="4" paddingY="5" paddingTop="3">
      <Flex justifyContent="space-between" alignItems="center">
        <SectionHeader title="Data" textStyle="heading/1" />
        <Flex>
          <Menu>
            <Tooltip label="Connect an external data source">
              <MenuButton as={Button} size="lg" variant="primary" leftIcon={<Icon name="add" />}>
                Connect
              </MenuButton>
            </Tooltip>
            <MenuList>
              <MenuItem
                leftIcon={<Icon name="data" />}
                primaryLabel="Database"
                onClick={() => {
                  analytics.capture(open_connector_modal_event, { from: "data_page" });
                  onOpenAddConnector();
                }}
              />
            </MenuList>
          </Menu>
        </Flex>
      </Flex>
      <Flex direction="column" flex="1" gap="3" marginTop="spacing-lg">
        <Tabs index={tabIndex}>
          <TabList>
            <Tab height="2.5rem" color="foreground.primary" fontWeight="medium" fontStyle="label/M/medium">
              Databases
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel padding="0" paddingTop="spacing-lg">
              <ConnectorTable
                isLoading={isLoading}
                data={tableData}
                onDeleteConnector={(connectorId) => {
                  setConnectorToDelete(connectorId);
                  onOpenDeleteModal();
                }}
                onRenameConnector={(connectorId) => {
                  setConnectorToRename(connectorId);
                  onOpenRenameModal();
                }}
                onSelectRow={navigateToSemanticModel}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
      <DeleteConfirmationModal
        headline="Delete Connector"
        notificationText="You are about to delete this Connector. This action cannot be undone. Are you sure?"
        buttonText="Delete Connector"
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setConnectorToDelete(null);
          onCloseDeleteModal();
        }}
        onDelete={async () => {
          if (!connectorToDelete) return;
          analytics.capture(delete_connector_event, { from: "modal" });
          await deleteResource(connectorToDelete);
          queryClient.invalidateQueries({ queryKey: ["connectors", teamId] });
        }}
      />
      <RenameItemModal
        title="Rename Connector"
        buttonText="Rename Connector"
        defaultName={tableData.find((connector) => connector.id === connectorToRename)?.name || ""}
        inputPlaceholder="Rename Connector"
        isOpen={isRenameModalOpen}
        onClose={() => {
          setConnectorToRename(null);
          onCloseRenameModal();
        }}
        onRename={async (newName) => {
          if (!connectorToRename) return;
          analytics.capture(rename_connector_event, { from: "modal" });
          await renameResource(connectorToRename, newName);
          queryClient.invalidateQueries({ queryKey: ["connectors", teamId] });
        }}
      />
      <ConnectorModal
        onOpenConnectorConfigModal={onOpenConnectorConfigModal}
        isOpen={isAddConnectorOpen}
        onClose={onCloseAddConnector}
      />
      <ConnectorConfigModal
        selectedConnectorType={selectedConnectorType}
        isOpen={isConnectorConfigOpen}
        onClose={onCloseConnectorConfig}
        teamId={teamId}
      />
    </Flex>
  );
};
